export const APP_THEMES = {
  current: {
    label: "Black",
    isLight: false,
    primaryColor: "#5e17eb",
    primaryMColor: "#7d45ef",
    pageBackground: "#000000",
    textColor: "#ffffff",
    mutedTextColor: "#dddddd",
    surfaceColor: "#111118",
    heroBackground: "#6f42c1",
    headerSurface: "#1e1e1e",
    headerShadow: "rgba(94, 23, 235, 0.3)",
  },
  white: {
    label: "White",
    isLight: true,
    primaryColor: "var(--primaryColor)",
    primaryMColor: "var(--primaryMColor)",
    pageBackground: "#ffffff",
    textColor: "#1a1a1a",
    mutedTextColor: "#444444",
    surfaceColor: "#f6f6f8",
    heroBackground: "#ebe9f3",
    headerSurface: "#ffffff",
    headerShadow: "rgba(94, 23, 235, 0.2)",
  },
  blush: {
    label: "Light Pink",
    isLight: true,
    primaryColor: "#b9406b",
    primaryMColor: "#d5658a",
    pageBackground: "#fcf0f4",
    textColor: "#2b1b22",
    mutedTextColor: "#5f4a53",
    surfaceColor: "#f9e6ed",
    heroBackground: "#f4dce6",
    headerSurface: "#fdf4f7",
    headerShadow: "rgba(185, 64, 107, 0.22)",
  },
  mint: {
    label: "Light Green",
    isLight: true,
    primaryColor: "#2f8f54",
    primaryMColor: "#50a573",
    pageBackground: "#f4fbf4",
    textColor: "#162419",
    mutedTextColor: "#3b5743",
    surfaceColor: "#e9f5ea",
    heroBackground: "#dff0e1",
    headerSurface: "#f8fdf8",
    headerShadow: "rgba(47, 143, 84, 0.24)",
  },
};

export const DEFAULT_THEME_KEY = "current";
export const THEME_STORAGE_KEY = "theme";

export const isValidThemeKey = (themeKey) =>
  typeof themeKey === "string" && Object.hasOwn(APP_THEMES, themeKey);

export const getStoredThemeKey = () => {
  if (typeof window === "undefined") {
    return DEFAULT_THEME_KEY;
  }

  try {
    const savedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isValidThemeKey(savedTheme) ? savedTheme : DEFAULT_THEME_KEY;
  } catch {
    return DEFAULT_THEME_KEY;
  }
};

export const setStoredThemeKey = (themeKey) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    if (isValidThemeKey(themeKey)) {
      window.localStorage.setItem(THEME_STORAGE_KEY, themeKey);
      return;
    }

    window.localStorage.removeItem(THEME_STORAGE_KEY);
  } catch {
    // Ignore localStorage failures (private mode / blocked storage)
  }
};

export const getThemeByKey = (themeKey) =>
  APP_THEMES[themeKey] || APP_THEMES[DEFAULT_THEME_KEY];

export const THEME_OPTIONS = Object.entries(APP_THEMES).map(
  ([value, theme]) => ({
    value,
    label: theme.label,
    swatchColor: theme.pageBackground,
    accentColor: theme.primaryColor,
  }),
);

export const createThemeVars = (theme) => ({
  "--primaryColor": theme.primaryColor,
  "--primaryMColor": theme.primaryMColor,
  "--instructorPageBg": theme.pageBackground,
  "--instructorText": theme.isLight ? "#111111" : "#ffffff",
  "--instructorMutedText": theme.mutedTextColor,
  "--instructorSurface": theme.surfaceColor,
  "--instructorHeroBg": theme.heroBackground,
  "--instructorContrastText": theme.isLight ? "#111111" : "#ffffff",
  "--instructorAccent": theme.primaryColor,
  "--instructorAccentSoft": theme.primaryMColor,
  "--shareModalBg": theme.surfaceColor,
  "--shareModalText": theme.isLight ? "#111111" : "#ffffff",
  "--shareModalSubText": theme.mutedTextColor,
  "--shareModalBorder": theme.primaryColor,
  "--shareModalCloseFilter": theme.isLight ? "none" : "invert(1)",
  "--shareModalIconBg": theme.isLight
    ? "rgba(0, 0, 0, 0.05)"
    : "rgba(255, 255, 255, 0.05)",
  "--shareModalIconBorder": theme.isLight
    ? "rgba(0, 0, 0, 0.18)"
    : "rgba(124, 58, 237, 0.25)",
});

export const createPageThemeVars = (theme) => ({
  ...createThemeVars(theme),
  backgroundColor: "var(--instructorPageBg)",
  color: "var(--instructorText)",
});

export const createHeaderThemeVars = (theme) => ({
  "--primaryColor": theme.primaryColor,
  "--primaryMColor": theme.primaryMColor,
  "--headerThemeSurface": theme.headerSurface,
  "--headerThemeText": theme.textColor,
  "--headerThemeShadow": theme.headerShadow,
});
